import { useCallback, useEffect, useState } from 'react';
import { isWeb } from 'smoothie';

import { useStorage } from '../../common/providers/StorageProvider';
import { captureException } from '../../logger/Logger';
import { SESSION_DURATION_MINUTES } from '../constants';
import {
    healStaleBiometricRegistration,
    isNoActiveBiometricRegistrationsError,
} from '../helpers';
import { useStytch } from './useAuth';

/**
 * Custom hook for handling biometric authentication.
 * This hook provides functions to check the availability of biometric authentication,
 * and to authenticate using biometrics.
 *
 * @returns An object containing the following properties:
 * - `hasBiometricFactor`: A boolean value indicating whether biometric authentication is available.
 * - `authenticateBiometrics`: A function to authenticate using biometrics.
 */
export const useBiometricAuthentication = () => {
    const [hasBiometricFactor, setHasBiometricFactor] = useState<
        boolean | undefined
    >(undefined);
    const stytch = useStytch();
    const storage = useStorage();

    const checkBiometricAvailability = useCallback(async () => {
        if (isWeb) {
            setHasBiometricFactor(false);
            return;
        }

        try {
            const isAvailable =
                await stytch.biometrics.isRegistrationAvailable();
            setHasBiometricFactor(isAvailable);
        } catch (err) {
            captureException(
                err instanceof Error
                    ? err
                    : new Error(`useBiometricAuthentication: ${String(err)}`),
            );
            setHasBiometricFactor(false);
        }
    }, [stytch.biometrics]);

    useEffect(() => {
        void checkBiometricAvailability();
    }, [checkBiometricAvailability]);

    /**
     * Authenticates using biometrics.
     *
     * @param onSuccess A callback function to be called when authentication is successful.
     * @param onFailure A callback function to be called when authentication fails.
     */
    const authenticateBiometrics = useCallback(
        async (onSuccess: () => void, onFailure: () => void) => {
            if (!hasBiometricFactor) {
                onFailure();
                return;
            }

            try {
                await stytch.biometrics.authenticate({
                    sessionDurationMinutes: SESSION_DURATION_MINUTES,
                    prompt: 'Sign in to Fruitful',
                    allowDeviceCredentials: true,
                });
                onSuccess();
            } catch (err) {
                if (isNoActiveBiometricRegistrationsError(err)) {
                    await healStaleBiometricRegistration(
                        stytch.biometrics,
                        storage,
                    );
                    setHasBiometricFactor(false);
                    onFailure();
                    return;
                }

                captureException(
                    err instanceof Error
                        ? err
                        : new Error(
                              `useBiometricAuthentication: ${String(err)}`,
                          ),
                );
                onFailure();
            }
        },
        [hasBiometricFactor, stytch.biometrics, storage],
    );

    return {
        hasBiometricFactor,
        authenticateBiometrics,
    };
};
